import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "catalina_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

// `withCredentials` is what sends the HttpOnly refresh cookie. The API and the
// app are on different sites, so without it the cookie is never attached and
// every refresh looks like a signed-out user.
const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Exchanges the refresh cookie for a new access token.
 *
 * Shared so that a burst of parallel 401s — the dashboard loads projects and
 * costs at once — produces one refresh rather than one per request, which
 * would rotate the cookie underneath itself and lose the race.
 */
let inFlight = null;

export function refreshSession() {
  if (!inFlight) {
    inFlight = axios
      .post(`${API}/auth/refresh`, null, { withCredentials: true })
      .then(({ data }) => {
        setToken(data.access_token);
        return data;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

/** Called when the session is definitively over, so the app can react. */
let onSessionLost = () => {};
export const setSessionLostHandler = (fn) => {
  onSessionLost = fn;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    // Only a 401 means "not you". A 502 from the database or a dropped
    // connection says nothing about the session, and treating those as a
    // logout is what used to sign people out on a reload.
    if (error.response?.status !== 401 || !original || original._retried) {
      return Promise.reject(error);
    }

    // The refresh call itself 401ing means the cookie is gone or revoked.
    if (original.url?.includes("/auth/refresh")) {
      clearToken();
      onSessionLost();
      return Promise.reject(error);
    }

    original._retried = true;
    try {
      await refreshSession();
      return api(original);
    } catch (refreshError) {
      clearToken();
      onSessionLost();
      return Promise.reject(refreshError);
    }
  },
);

/**
 * `fetch` with the same session handling as the axios instance.
 *
 * The generation stream and the ZIP download cannot go through axios — one
 * needs a readable stream and the other a blob — and they used to read the
 * token straight out of localStorage. That left them as the only calls in the
 * app with no refresh: starting a generation on an hour-old token failed with
 * a bare "Server error (401)".
 */
export async function authedFetch(url, init = {}) {
  const send = (token) =>
    fetch(url, {
      ...init,
      credentials: "include",
      headers: { ...(init.headers ?? {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });

  let response = await send(getToken());
  if (response.status !== 401) return response;

  try {
    const { access_token } = await refreshSession();
    response = await send(access_token);
  } catch {
    clearToken();
    onSessionLost();
    return response;
  }

  if (response.status === 401) {
    clearToken();
    onSessionLost();
  }
  return response;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
